import React from 'react';
import { HighlightedText } from './styles';

/**
 * Highlights matching text in option labels
 * Highlights individual words from the search term, including word stems
 * @param text - The text to highlight matches in
 * @param searchTerm - The search term to find and highlight
 * @returns JSX element with highlighted matches
 */
export const highlightText = (
  text: string,
  searchTerm: string
): string | JSX.Element => {
  if (!searchTerm.trim()) return text;

  const searchWords = searchTerm
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length > 0);

  if (searchWords.length === 0) return text;

  const matches: Array<{ start: number; end: number; word: string }> = [];

  // Find matches for each search word
  for (const searchWord of searchWords) {
    // First, try exact word match with word boundaries
    const exactRegex = new RegExp(
      `\\b${searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
      'gi'
    );
    let match;
    while ((match = exactRegex.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      matches.push({ start, end, word: match[0] });
    }

    // Also try word stems (e.g., "pair" matches "pairing", "paired")
    const stem = searchWord.replace(/(ing|ed|er|est|s|es)$/i, '');
    if (stem.length >= 3) {
      const stemRegex = new RegExp(
        `\\b${stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\w*\\b`,
        'gi'
      );
      let stemMatch;
      while ((stemMatch = stemRegex.exec(text)) !== null) {
        const start = stemMatch.index;
        const end = start + stemMatch[0].length;
        // Only add if not already matched by exact match
        const isExactMatch = matches.some(
          (m) => m.start === start && m.end === end
        );
        if (!isExactMatch) {
          matches.push({ start, end, word: stemMatch[0] });
        }
      }
    }
  }

  // Sort matches by start position
  matches.sort((a, b) => a.start - b.start);

  // Remove overlapping matches (keep the first/longest)
  const nonOverlappingMatches: Array<{
    start: number;
    end: number;
    word: string;
  }> = [];
  for (const match of matches) {
    const overlaps = nonOverlappingMatches.some(
      (existing) =>
        (match.start >= existing.start && match.start < existing.end) ||
        (match.end > existing.start && match.end <= existing.end) ||
        (match.start <= existing.start && match.end >= existing.end)
    );

    if (!overlaps) {
      nonOverlappingMatches.push(match);
    }
  }

  // Sort again after removing overlaps
  nonOverlappingMatches.sort((a, b) => a.start - b.start);

  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;

  for (const match of nonOverlappingMatches) {
    // Add text before the match
    if (match.start > lastIndex)
      parts.push(text.substring(lastIndex, match.start));

    parts.push(
      <HighlightedText key={match.start}>
        {text.substring(match.start, match.end)}
      </HighlightedText>
    );

    lastIndex = match.end;
  }

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts.length > 0 ? parts : text}</>;
};
